import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { ICellEditorParams } from "ag-grid-community";

interface GridDropDownOption {
	value: string;
	label: string;
}

interface GridDropDownFormProps extends ICellEditorParams {
	optionList: GridDropDownOption[];
	onChange?: (newValue: string, params: ICellEditorParams) => void;
	onCommitAfter?: (params: ICellEditorParams, key: "Enter" | "Tab") => void;
}

const GRID_DROPDOWN_Z_INDEX = 10060;

export const GridDropDownForm = forwardRef<any, GridDropDownFormProps>((props, ref) => {
	const values = useMemo(() => (
		props.optionList.map((option) => option.value)
	), [props.optionList]);

	const initialValue = props.value == null ? "" : String(props.value);
	const initialIndex = Math.max(0, values.findIndex((v) => String(v) === initialValue));

	const [selectedIndex, setSelectedIndex] = useState(initialIndex === -1 ? 0 : initialIndex);
	const rootRef = useRef<HTMLDivElement>(null);
	const selectedIndexRef = useRef(initialIndex === -1 ? 0 : initialIndex);
	const selectedValueRef = useRef<string>(values[initialIndex === -1 ? 0 : initialIndex] ?? "");

	useEffect(() => {
		selectedIndexRef.current = selectedIndex;
		selectedValueRef.current = values[selectedIndex] ?? "";
	}, [selectedIndex, values]);

	useEffect(() => {
		const applyPopupZIndex = () => {
			const popupElement = rootRef.current?.closest<HTMLElement>('.ag-popup, .ag-popup-child, .ag-popup-editor');
			if (popupElement) {
				popupElement.style.zIndex = String(GRID_DROPDOWN_Z_INDEX);
			}
		};

		applyPopupZIndex();
		const frameId = window.requestAnimationFrame(applyPopupZIndex);

		return () => window.cancelAnimationFrame(frameId);
	}, []);

	const setSelection = useCallback((nextIndex: number) => {
		const safeIndex = Math.max(0, Math.min(nextIndex, values.length - 1));
		selectedIndexRef.current = safeIndex;
		selectedValueRef.current = values[safeIndex] ?? "";
		setSelectedIndex(safeIndex);
	}, [values]);

	useImperativeHandle(ref, () => ({
		getValue: () => selectedValueRef.current,
		isPopup: () => true,
		getPopupPosition: () => "under",
		afterGuiAttached: () => {
			window.setTimeout(() => rootRef.current?.focus(), 0);
		},
	}));

	const commitAndClose = useCallback((nextIndex: number) => {
		const safeIndex = Math.max(0, Math.min(nextIndex, values.length - 1));
		const nextValue = values[safeIndex] ?? "";
		setSelection(safeIndex);
		selectedValueRef.current = nextValue;

		if (props.onChange) {
			props.onChange(nextValue, props);
		}

		if (props.stopEditing) {
			props.stopEditing();
		} else {
			props.api.stopEditing();
		}
	}, [props, setSelection, values]);

	const handleEditorKeyDown = useCallback((key: string, event: Pick<KeyboardEvent | React.KeyboardEvent<HTMLDivElement>, "preventDefault" | "stopPropagation">) => {
		if (values.length === 0) return;

		if (key === "ArrowDown") {
			event.preventDefault();
			event.stopPropagation();
			setSelection(selectedIndexRef.current + 1);
			return;
		}
		if (key === "ArrowUp") {
			event.preventDefault();
			event.stopPropagation();
			setSelection(selectedIndexRef.current - 1);
			return;
		}
		if (key === "Enter" || key === "Tab") {
			event.preventDefault();
			event.stopPropagation();

			const nextValue = values[selectedIndexRef.current] ?? "";
			selectedValueRef.current = nextValue;

			if (props.onChange) {
				props.onChange(nextValue, props);
			}

			if (props.stopEditing) {
				props.stopEditing();
			} else {
				props.api.stopEditing();
			}
			props.onCommitAfter?.(props, key);
			return;
		}
		if (key === "Escape") {
			event.preventDefault();
			event.stopPropagation();
			if (props.stopEditing) {
				props.stopEditing(true);
			} else {
				props.api.stopEditing(true);
			}
		}
	}, [props, setSelection, values]);

	useEffect(() => {
		const handleDocumentKeyDown = (event: KeyboardEvent) => {
			if (!["ArrowDown", "ArrowUp", "Enter", "Tab", "Escape"].includes(event.key)) return;
			if (rootRef.current?.contains(document.activeElement)) return;

			handleEditorKeyDown(event.key, event);
			event.stopImmediatePropagation();
		};

		document.addEventListener("keydown", handleDocumentKeyDown, true);

		return () => document.removeEventListener("keydown", handleDocumentKeyDown, true);
	}, [handleEditorKeyDown]);

	const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
		handleEditorKeyDown(e.key, e);
	}, [handleEditorKeyDown]);

	return (
		<div
			ref={rootRef}
			tabIndex={0}
			className="ag-custom-dropdown-editor ag-custom-component-popup"
			onKeyDown={handleKeyDown}
			onMouseDown={() => rootRef.current?.focus()}
			style={{
				minWidth: `${Math.max(props.column?.getActualWidth?.() ?? 140, 140)}px`,
				position: "relative",
				zIndex: GRID_DROPDOWN_Z_INDEX,
			}}
		>
			{values.length === 0 ? (
				<div
					className="ag-custom-dropdown-option is-disabled"
					aria-disabled="true"
					onMouseDown={(e) => {
						e.preventDefault();
						e.stopPropagation();
					}}
					style={{
						color: "#9ca3af",
						cursor: "not-allowed",
					}}
				>
					(없음)
				</div>
			) : values.map((value, idx) => {
				const label = props.optionList.find((opt) => String(opt.value) === String(value))?.label ?? value;
				const isActive = idx === selectedIndex;
				return (
					<div
						key={`${value}-${idx}`}
						className={`ag-custom-dropdown-option${isActive ? " is-active" : ""}`}
						onMouseDown={(e) => {
							e.preventDefault();
							e.stopPropagation();
							commitAndClose(idx);
						}}
					>
						{label}
					</div>
				);
			})}
		</div>
	);
});

GridDropDownForm.displayName = "GridDropDownForm";